import { DRAW_ORDER, EDGE_TEXT_SHARE, mutableLabel, type MutableLabel } from './meshAnnotations';
import { GROUP_JOIN } from './openaiBlenderApi';

type THREE_NS = typeof import('three');
type Vec3 = [number, number, number];

// WHERE A WRITTEN MODEL'S DIMENSIONS ARE MEASURED, drawn over it as thin red lines.
//
// Each dimension of a written script says where it is measured with measure() (see
// written_toolkit.measure): two points, in millimetres, in the file's own frame. Here they are
// segments with a tick at each end and the number beside them — the same number the field under
// the viewer holds, so which segment "Back upright neck width" is stops being a guess.
//
// DRAWN THROUGH THE MESH (no depth test), as every other annotation in this scene is: a width
// measured across a leg is inside the leg's silhouette, and a line the leg hid would be a line
// that could only be seen from the one side it happens to face.
export type DimensionLine = { name: string; start: Vec3; end: Vec3 };

export type DimensionLineSpec = {
  lines: DimensionLine[];
  // Each dimension's group (the mesh it sizes, or "Whole object"), its number and its label.
  about: Record<string, { group: string; value: number; label: string }>;
  // The groups whose lines are shown, and the one dimension a focused field is about — shown
  // whatever its group, and drawn over the rest.
  on: string[];
  focus: string | null;
};

const RED = 0xe11d48;
// A tick either side of each end, as a share of the picture — see `widest` in update.
const TICK_SHARE = 0.008;
// HOW THICK, IN SCREEN PIXELS, whatever the zoom. A plain three.js line is one pixel on every
// browser — WebGL ignores its width — which read as a hairline against a wooden leg.
const LINE_PX = 2.5;
const FOCUS_PX = 3.5;

// THE FAT-LINE CLASSES, loaded once and only by a viewer that draws lines. They import three
// themselves, and three is a dynamic import everywhere in this app (see meshScene.loadDeps).
type Fat = {
  LineSegments2: typeof import('three/examples/jsm/lines/LineSegments2.js').LineSegments2;
  LineSegmentsGeometry: typeof import('three/examples/jsm/lines/LineSegmentsGeometry.js').LineSegmentsGeometry;
  LineMaterial: typeof import('three/examples/jsm/lines/LineMaterial.js').LineMaterial;
};
let fatLoad: Promise<Fat> | null = null;
const loadFat = () => (fatLoad ??= Promise.all([
  import('three/examples/jsm/lines/LineSegments2.js'),
  import('three/examples/jsm/lines/LineSegmentsGeometry.js'),
  import('three/examples/jsm/lines/LineMaterial.js'),
]).then(([a, b, c]) => ({
  LineSegments2: a.LineSegments2, LineSegmentsGeometry: b.LineSegmentsGeometry, LineMaterial: c.LineMaterial,
})));

export function makeDimensionLines(THREE: THREE_NS) {
  const group = new THREE.Group();
  const labels: MutableLabel[] = [];
  // Which dimension each label is showing, by label; and the dimensions whose name somebody
  // clicked a number to read — every copy of it says so, until it is clicked again.
  const labelNames: string[] = [];
  const named = new Set<string>();
  let spec: DimensionLineSpec | null = null;
  let disposed = false;

  // The lines shown, and the focused one over them. The rest go pale while a field is focused,
  // so the one being typed into is the one that reads. Built once the fat-line classes are in;
  // until then the last positions wait in `pending`.
  type Layer = { draw: (positions: number[], opacity: number) => void; dispose: () => void };
  let layers: { base: Layer; focused: Layer } | null = null;
  let pending: { plain: number[]; lit: number[]; opacity: number } | null = null;
  const paint = () => {
    if (!layers || !pending) return;
    layers.base.draw(pending.plain, pending.opacity);
    layers.focused.draw(pending.lit, 1);
  };
  void loadFat().then((fat) => {
    if (disposed) return;
    const layer = (px: number, order: number): Layer => {
      const geometry = new fat.LineSegmentsGeometry();
      const material = new fat.LineMaterial({
        color: RED, linewidth: px, depthTest: false, transparent: true, toneMapped: false,
      });
      const segments = new fat.LineSegments2(geometry, material);
      segments.renderOrder = order;
      segments.frustumCulled = false;
      // The width is in pixels of the canvas it is drawn into, which the material has to be told.
      segments.onBeforeRender = (renderer) => { renderer.getSize(material.resolution); };
      group.add(segments);
      return {
        draw: (positions, opacity) => {
          segments.visible = positions.length > 0;
          if (positions.length) geometry.setPositions(positions);
          material.opacity = opacity;
        },
        dispose: () => { geometry.dispose(); material.dispose(); },
      };
    };
    layers = { base: layer(LINE_PX, DRAW_ORDER.label - 2), focused: layer(FOCUS_PX, DRAW_ORDER.label - 1) };
    paint();
  });

  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const dir = new THREE.Vector3();
  const across = new THREE.Vector3();
  const UP = new THREE.Vector3(0, 0, 1);
  const SIDE = new THREE.Vector3(1, 0, 0);

  const label = (i: number) => {
    while (labels.length <= i) {
      const made = mutableLabel(THREE, RED);
      group.add(made.sprite);
      labels.push(made);
    }
    return labels[i];
  };

  return {
    group,
    set: (next: DimensionLineSpec | null) => { spec = next; },
    // `place(point, group)` writes a file point (millimetres) into the annotation frame, or
    // answers false where the mesh it is on is not in the picture. `widest` is the picture's
    // size, which ticks and words are drawn against.
    update: (place: (out: import('three').Vector3, point: Vec3, group: string) => boolean,
      widest: number) => {
      const plain: number[] = [];
      const lit: number[] = [];
      const placed: import('three').Vector3[] = [];
      let used = 0;
      const tick = widest * TICK_SHARE;
      for (const line of spec?.lines ?? []) {
        const about = spec!.about[line.name];
        if (!about) continue;
        const isFocus = spec!.focus === line.name;
        // A group shared by two or three meshes ("Seat & Side rail (Left)") shows with any of them.
        const shown = spec!.on.includes(about.group)
          || about.group.split(GROUP_JOIN).some((mesh) => spec!.on.includes(mesh));
        if (!(isFocus || shown)) continue;
        if (!place(a, line.start, about.group) || !place(b, line.end, about.group)) continue;
        const out = isFocus ? lit : plain;
        out.push(a.x, a.y, a.z, b.x, b.y, b.z);
        // THE TICKS, square to the segment and as level as it allows.
        dir.subVectors(b, a);
        across.crossVectors(dir, Math.abs(dir.clone().normalize().z) > 0.9 ? SIDE : UP);
        if (across.lengthSq() > 0) {
          across.setLength(tick);
          for (const end of [a, b]) {
            out.push(end.x - across.x, end.y - across.y, end.z - across.z,
              end.x + across.x, end.y + across.y, end.z + across.z);
          }
        }
        // THE NUMBER AT THE MIDDLE — or, where another number is already there, just past the
        // segment's far end. A leg's width and depth are measured across its centre and cross
        // there, and two plates on one point is one number hidden under the other.
        const height = widest * EDGE_TEXT_SHARE * (isFocus ? 1.1 : 0.8);
        labelNames[used] = line.name;
        const words = label(used++);
        const at = words.sprite.position.addVectors(a, b).multiplyScalar(0.5);
        if (placed.some((p) => p.distanceTo(at) < height * 2) && dir.lengthSq() > 0) {
          at.copy(b).addScaledVector(dir.normalize(), height * 2.5);
        }
        placed.push(at.clone());
        words.sprite.visible = true;
        words.set(`${about.value}`, height, isFocus || named.has(line.name) ? about.label : undefined);
      }
      for (let i = used; i < labels.length; i++) labels[i].sprite.visible = false;
      pending = { plain, lit, opacity: spec?.focus ? 0.35 : 0.9 };
      paint();
    },
    // THE DIMENSION WHOSE NUMBER IS UNDER THE POINTER, or null — the ray already cast from it.
    pick: (raycaster: import('three').Raycaster): string | null => {
      for (let i = 0; i < labels.length; i++) {
        if (labels[i].sprite.visible && raycaster.intersectObject(labels[i].sprite, false).length) {
          return labelNames[i];
        }
      }
      return null;
    },
    // Show a dimension's name under its number, or stop.
    toggleName: (name: string) => {
      if (named.has(name)) named.delete(name);
      else named.add(name);
    },
    dispose: () => {
      disposed = true;
      layers?.base.dispose();
      layers?.focused.dispose();
      for (const one of labels) one.dispose();
    },
  };
}
